import React, { useState } from 'react';
import { chatWithRodman } from './api';

const RodmanTest = () => {
    const [responses, setResponses] = useState({});
    const [loading, setLoading] = useState({});

    // ALL possible Rodman queries extracted from Rodman.js
    const testQueries = {
        "Dynamic Suggestions (Games Today)": [
            { text: "Show me your highest confidence locks today", queryType: "confidence_locks" },
            { text: "Any upset alerts brewing today?", queryType: "upset_alerts" },
            { text: "What's your hottest streak right now?", queryType: "hot_streak" },
            { text: "Show me where your model is most accurate", queryType: "accuracy_showcase" }
        ],
        "Dynamic Suggestions (Weekend: Fri-Mon)": [
            { text: "How did your predictions perform this week?", queryType: "performance_review" },
            { text: "Show me the week's biggest power ranking movers", queryType: "power_movers" },
            { text: "What teams exceeded expectations this week?", queryType: "surprise_teams" },
            { text: "Looking ahead - what's the week's storyline?", queryType: "week_narrative" }
        ],
        "Dynamic Suggestions (Midweek: Tue-Thu)": [
            { text: "Which teams are surprising you this season?", queryType: "surprise_teams" },
            { text: "Show me recent NBA and Premier League form", queryType: "recent_form" },
            { text: "What's trending in the power rankings?", queryType: "power_rankings" },
            { text: "Show me your best accuracy spots", queryType: "accuracy_showcase" }
        ],
        "Fallback Suggestions (Weekend)": [
            { text: "How did your predictions perform this week?", queryType: "general" },
            { text: "Show me the week's biggest power ranking movers", queryType: "general" },
            { text: "Which teams exceeded expectations this week?", queryType: "general" },
            { text: "What's your track record this season?", queryType: "general" }
        ],
        "Fallback Suggestions (Weekday)": [
            { text: "How are you performing this week?", queryType: "general" },
            { text: "Show me recent NBA and Premier League form", queryType: "general" },
            { text: "Which teams are surprising you this season?", queryType: "general" },
            { text: "What's trending in the power rankings?", queryType: "general" }
        ],
        "Quick Actions (Games Today)": [
            { text: "Show me your highest confidence locks today", queryType: "confidence_locks", button: "🔥 Today's locks" },
            { text: "Any upset alerts brewing today?", queryType: "upset_alerts", button: "⚡ Upset alerts" },
            { text: "What's your hottest streak right now?", queryType: "hot_streak", button: "🔥 Hot streak" },
            { text: "Show me where your model is most accurate", queryType: "accuracy_showcase", button: "🎯 Accuracy showcase" }
        ],
        "Quick Actions (No Games)": [
            { text: "How did your predictions perform this week?", queryType: "weekly_performance", button: "📊 This week's performance" },
            { text: "Which teams are trending up in power rankings?", queryType: "power_movers", button: "📈 Power movers" },
            { text: "Show me your best accuracy spots", queryType: "accuracy_showcase", button: "🎯 Best accuracy spots" },
            { text: "What's the week's biggest storylines?", queryType: "week_narrative", button: "📰 Week's storylines" }
        ],
        "Game Context Examples": [
            { text: "Give me your prediction for Lakers vs Warriors", queryType: "game_analysis" },
            { text: "Analyze the Lakers vs Warriors game - what happened? They scored 112-108.", queryType: "game_analysis" },
            { text: "Tell me about Manchester City vs Arsenal - who wins and why?", queryType: "game_analysis" }
        ]
    };

    const testQuery = async (queryObj, category) => {
        const query = queryObj.text || queryObj;
        const queryType = queryObj.queryType || 'general';
        const queryKey = `${category}-${query}`;
        setLoading(prev => ({ ...prev, [queryKey]: true }));
        
        try {
            // Test with preset flag for curated responses
            const response = await chatWithRodman(query, null, true, queryType);
            
            setResponses(prev => ({ 
                ...prev, 
                [queryKey]: {
                    query,
                    response: response.response,
                    confidence: response.confidence,
                    sport_detected: response.sport_detected,
                    queryType,
                    buttonText: queryObj.button || null,
                    timestamp: new Date().toLocaleTimeString()
                }
            }));
        } catch (error) {
            console.error('Error testing query:', error);
            setResponses(prev => ({ 
                ...prev, 
                [queryKey]: {
                    query,
                    response: `Error: ${error.message}`,
                    error: true,
                    timestamp: new Date().toLocaleTimeString()
                }
            }));
        } finally {
            setLoading(prev => ({ ...prev, [queryKey]: false }));
        }
    };

    const testAllQueries = async () => {
        for (const [category, queries] of Object.entries(testQueries)) {
            for (const query of queries) {
                await testQuery(query, category);
                // Small delay between requests to avoid overwhelming the API
                await new Promise(resolve => setTimeout(resolve, 1000));
            }
        }
    };

    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: '#1a1a1a',
            color: 'white',
            padding: '20px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}>
            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                <h1 style={{ 
                    fontSize: '2.5rem', 
                    marginBottom: '10px',
                    background: 'linear-gradient(45deg, #007AFF, #00D4FF)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                }}>
                    🧪 RODMAN AI TEST LAB
                </h1>
                <p style={{ color: '#888', fontSize: '1.1rem' }}>
                    Test all possible Rodman queries and responses with real 82.1% accuracy data
                </p>
            </div>

            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                <button 
                    onClick={testAllQueries}
                    style={{
                        padding: '12px 24px',
                        background: 'linear-gradient(45deg, #007AFF, #0051D5)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        boxShadow: '0 4px 15px rgba(0, 122, 255, 0.3)',
                        transition: 'transform 0.2s'
                    }}
                    onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                    onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                >
                    🚀 Test All Queries ({Object.values(testQueries).flat().length} total)
                </button>
            </div>

            <div style={{ 
                maxWidth: '1200px', 
                margin: '0 auto',
                display: 'grid',
                gap: '30px'
            }}>
                {Object.entries(testQueries).map(([category, queries]) => (
                    <div key={category} style={{
                        backgroundColor: '#2a2a2a',
                        borderRadius: '12px',
                        padding: '20px',
                        border: '1px solid #333'
                    }}>
                        <h2 style={{ 
                            color: '#007AFF', 
                            borderBottom: '2px solid #007AFF', 
                            paddingBottom: '10px',
                            marginBottom: '20px',
                            fontSize: '1.3rem',
                            fontWeight: 'bold'
                        }}>
                            {category} ({queries.length} queries)
                        </h2>
                        
                        <div style={{ display: 'grid', gap: '15px' }}>
                            {queries.map((queryObj, index) => {
                                const query = queryObj.text || queryObj;
                                const queryKey = `${category}-${query}`;
                                const response = responses[queryKey];
                                const isLoading = loading[queryKey];
                                
                                return (
                                    <div key={index} style={{ 
                                        backgroundColor: '#1a1a1a',
                                        border: '1px solid #444',
                                        borderRadius: '8px',
                                        padding: '15px',
                                        transition: 'border-color 0.2s'
                                    }}>
                                        <div style={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between', 
                                            alignItems: 'flex-start',
                                            marginBottom: '10px',
                                            gap: '10px'
                                        }}>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ color: '#fff', fontSize: '14px', fontWeight: 'bold', marginBottom: '4px' }}>
                                                    {query}
                                                </div>
                                                {queryObj.button && (
                                                    <div style={{ color: '#007AFF', fontSize: '12px' }}>
                                                        Button: {queryObj.button}
                                                    </div>
                                                )}
                                                <div style={{ color: '#666', fontSize: '11px' }}>
                                                    Type: {queryObj.queryType || 'general'}
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => testQuery(queryObj, category)}
                                                disabled={isLoading}
                                                style={{
                                                    padding: '6px 12px',
                                                    backgroundColor: isLoading ? '#555' : '#007AFF',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    cursor: isLoading ? 'not-allowed' : 'pointer',
                                                    fontSize: '11px',
                                                    fontWeight: 'bold',
                                                    minWidth: '80px'
                                                }}
                                            >
                                                {isLoading ? '⏳ Testing...' : '🧪 Test'}
                                            </button>
                                        </div>
                                        
                                        {response && (
                                            <div style={{
                                                backgroundColor: response.error ? '#2d1b1b' : '#1e2a1e',
                                                padding: '12px',
                                                borderRadius: '6px',
                                                border: response.error ? '1px solid #ff6b6b' : '1px solid #4a6741',
                                                marginTop: '10px'
                                            }}>
                                                <div style={{ 
                                                    fontSize: '11px', 
                                                    color: '#888',
                                                    marginBottom: '8px',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    flexWrap: 'wrap',
                                                    gap: '8px'
                                                }}>
                                                    <span>🕐 {response.timestamp}</span>
                                                    <span>🎯 {response.confidence ? `${Math.round(response.confidence * 100)}%` : 'N/A'}</span>
                                                    <span>🏆 {response.sport_detected || 'general'}</span>
                                                    <span>🔍 {response.queryType || 'general'}</span>
                                                </div>
                                                <div style={{ 
                                                    fontSize: '13px', 
                                                    lineHeight: '1.5',
                                                    color: response.error ? '#ff9999' : '#e0e0e0',
                                                    whiteSpace: 'pre-wrap'
                                                }}>
                                                    {response.response}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default RodmanTest;
